import { useIntl } from 'react-intl';
import { ApiResponseStatus, ErrorPhrase } from '@constants/common';
import { useError } from '@hooks/useError';
import { ResponseData } from '@models/common';
import {
  isFailedFetchError,
  isPageReloadScheduled,
  logError,
  RESPONSE_STATUS_ERROR_TYPE,
} from '@utils/errors';

export type TError = unknown;

const isDataWithStatus = (data: unknown): data is ResponseData<typeof data> =>
  (data as ResponseData<typeof data>) &&
  (data as ResponseData<typeof data>).status !== undefined;

const safeStringify = (value: unknown): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '[Unserializable object]';
    }
  }

  return String(value);
};

export const useHandleErrors = () => {
  const { setError } = useError();
  const { formatMessage } = useIntl();

  const handleErrorsOnSuccess = <TData>(
    data: TData,
    variables?: unknown,
    operation?: string
  ) => {
    if (!isDataWithStatus(data)) {
      return;
    }

    const statusText =
      data.message ?? formatMessage({ id: ErrorPhrase.BASE_ERROR });

    if (data.status !== ApiResponseStatus.SUCCESS) {
      setError(statusText);
      logError(new Error(statusText), {
        statusText,
        variables,
        operation,
        statusCode: data.status,
        response: safeStringify(data),
        errorType: RESPONSE_STATUS_ERROR_TYPE,
      });
    }
  };

  const handleErrorsOnError = (
    error: TError,
    variables?: unknown,
    operation?: string
  ) => {
    // The page is about to reload (stale bundle) — the error is already handled.
    if (isPageReloadScheduled()) {
      return;
    }

    const { response, message } = error as {
      response: ResponseData<unknown>;
      message: string;
    };
    const isFailedFetch = isFailedFetchError(error);

    const statusText = isFailedFetch
      ? formatMessage({ id: ErrorPhrase.SERVICE_UNAVAILABLE })
      : message ||
        response?.message ||
        formatMessage({ id: ErrorPhrase.BASE_ERROR });

    setError(statusText);
    logError(error, {
      statusText,
      variables,
      operation,
      statusCode: response?.status,
      response: safeStringify(response),
      errorType: isFailedFetch ? 'failed_fetch' : 'application_error',
    });
  };

  return { handleErrorsOnSuccess, handleErrorsOnError };
};
